import { HomeConfig } from '@tenants/types/tenant';
import { LOCALE_EXP_COMMERCIAL_PATHS } from 'types/locales';
import { RootDomain } from 'constants/domains';

export function createHomeConfig(_rootDomain: RootDomain): HomeConfig {
  const homeConfig: HomeConfig = {
    heroVideo: '/next/assets/videos/commercial/commercial-hero.webm',
    heroImage: '/next/assets/images/commercial/hero.jpg',
    heroImageAlt: 'Commercial real estate hero image',
    joinLink: '/become-an-advisor',
    carouselImages: [
      '/next/assets/images/carousel/exp-commercial-1.mp4',
      '/next/assets/images/carousel/exp-commercial-2.jpg',
      '/next/assets/images/carousel/exp-commercial-3.jpg',
    ],
    testimonialBlock: {
      backgroundImage: '/next/assets/images/agent-search/exp-mobile-banner-white-flag.jpg',
      heading: 'Hear From Our Commercial Experts',
      testimonials: [
        {
          image: '/next/assets/images/in-service/inservice-agent1.jpg',
          alt: 'Agent 1',
          name: 'Brittany Sims',
          company: 'eXp Realty, Virginia',
          text: '"I\'m absolutely loving eXp. From the systems to the support, being able to do real estate while knowing I can build my vision is such an awesome feeling. Everything just feels very aligned."',
          index: 0,
        },
      ],
    },
    joinUsSection: {
      link: '/become-an-advisor',
      title: 'Why Join eXp Commercial',
      title2: 'Commercial Brokerage Program',
      description: 'When you join eXp Commercial, you become part of a global family of agents and partners dedicated to transforming the commercial real estate experience. Learn more about how we empower our agents with world-class education, strategic partnership and stock awards.',
      imageUrl: '/next/assets/images/join-us-commercial.jpg',
      buttonText: 'Learn More',
    },
    contentCards: {
      sectionTitleParts: ['Commercial', 'Real Estate', 'Experts'],
      cards: [
        {
          title: 'Commercial Services',
          image: '/next/assets/images/content-cards/commercial-services.jpg',
          titleColor: 'rgba(255, 255, 255, 0.8)',
          backgroundColor: '#163259',
          content: 'From office spaces to industrial properties, our dedicated commercial experts provide comprehensive services tailored to your business needs. We specialize in all types of commercial real estate transactions.',
          link: '/my-services',
          desktopTransform: 'rotate(-5deg) translateY(-1.5em)',
          tabletTransform: 'none',
          rounded: true,
          altText: 'Modern commercial building with glass facade.',
          buttonText: 'Explore Services',
        },
        {
          title: 'Commercial Listings',
          image: '/next/assets/images/content-cards/commercial-listings.jpg',
          titleColor: 'rgba(255, 255, 255, 0.7)',
          backgroundColor: '#6C82A0',
          content: 'Access a comprehensive database of commercial properties. Our platform provides detailed information about office spaces, retail locations, industrial properties, and more.',
          link: '/listings',
          tabletTransform: 'translateY(3em)',
          translateYTablet: '50',
          altText: 'Commercial property listing showcase',
          buttonText: 'View Listings',
        },
        {
          title: 'Expert Advisors',
          image: '/next/assets/images/content-cards/expert-advisors.jpg',
          titleColor: '#0C0F24',
          backgroundColor: '#DAE0E7',
          content: 'Work with experienced commercial real estate professionals who understand your business needs. Our advisors specialize in various commercial property types and market segments.',
          link: '/experts',
          desktopTransform: 'rotate(2deg) translateY(-1.5em)',
          tabletTransform: 'none',
          translateYDesktop: '-20',
          altText: 'Professional commercial real estate advisor in a meeting',
          buttonText: 'Find an Expert',
        },
        {
          title: 'Commercial Insights',
          image: '/next/assets/images/content-cards/commercial-insights.jpg',
          titleColor: '#0C0F24',
          backgroundColor: '#EEEEEE',
          content: 'Stay informed with the latest commercial real estate market trends, investment opportunities, and industry insights. Access valuable resources designed for commercial property investors and businesses.',
          link: '/insights',
          desktopTransform: 'rotate(7deg) translateY(2em)',
          tabletTransform: 'translateY(3em)',
          translateYDesktop: '40',
          translateYTablet: '50',
          rounded: true,
          altText: 'Commercial real estate market analysis',
          buttonText: 'Read Insights',
        },
      ],
    },
    autoplaySlides: {
      title: 'Commercial Advisors',
      description: 'Our Advisor Directory helps you quickly find trusted experts with specialized knowledge across all commercial asset types and markets.',
      linkText: 'Find a Commercial Advisor',
      linkPath: '/experts',
      slides: [
        {
          image: '/next/assets/images/exp-commercial/calvin-wong.webp',
          name: 'Calvin Wong',
          description: 'Advisor, Senior Vice President, Texas',
        },
        {
          image: '/next/assets/images/exp-commercial/pamela-junge.webp',
          name: 'Pamela Junge',
          description: 'Advisor, Vice President, Nevada',
        },
        {
          image: '/next/assets/images/exp-commercial/bob-richards.webp',
          name: 'Bob Richards',
          description: 'Advisor, Wisconsin',
        },
        {
          image: '/next/assets/images/exp-commercial/chadwick-wegner.webp',
          name: 'Chadwick Wegner',
          description: 'Advisor, Wisconsin',
        },
        {
          image: '/next/assets/images/exp-commercial/michelle-pauley.webp',
          name: 'Michelle Pauley',
          description: 'Senior Advisor, Arizona',
        },
        {
          image: '/next/assets/images/exp-commercial/daren-dudgeon.webp',
          name: 'Daren Dudgeon',
          description: 'Advisor, Wisconsin',
        },
      ],
    },
    tiltedCards: {
      heading: {
        title: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_HEADING_TITLE,
        buttonText: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_HEADING_BUTTONTEXT,
        buttonLink: '/our-services',
      },
      backgroundColor: '#f9f9f9',
      cards: [
        {
          title: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_INVESTMENTSALES_TITLE,
          image: '/next/assets/images/exp-commercial/investment-sales.webp',
          imgAlt: 'Professional reviewing investment data and performance charts in a bright modern workspace.',
          description: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_INVESTMENTSALES_DESCRIPTION,
          backgroundColor: '#EEEEEE',
          link: '/our-services/investment-sales',
        },
        {
          title: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_SELLERREPRESENTATION_TITLE,
          image: '/next/assets/images/exp-commercial/seller-representation.webp',
          imgAlt: 'Group of professionals having a discussion around a conference table in a modern office.',
          description: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_SELLERREPRESENTATION_DESCRIPTION,
          backgroundColor: '#D1D7BA',
          link: '/our-services/seller-representation',
        },
        {
          title: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_BUYERREPRESENTATION_TITLE,
          image: '/next/assets/images/exp-commercial/buyer-representation.webp',
          imgAlt: 'Professionals touring a stylish, open-concept commercial office space with natural light and modern decor.',
          description: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_BUYERREPRESENTATION_DESCRIPTION,
          backgroundColor: '#3B5054',
          isDark: true,
          link: '/our-services/buyer-representation',
        },
        {
          title: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_LANDLORDREPRESENTATION_TITLE,
          image: '/next/assets/images/exp-commercial/landlord-representation.webp',
          imgAlt: 'Smiling woman holding a folder, speaking with two colleagues in a modern office hallway.',
          description: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_LANDLORDREPRESENTATION_DESCRIPTION,
          backgroundColor: '#DEDEDE',
          link: '/our-services/landlord-representation',
        },
        {
          title: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_TENANTREPRESENTATION_TITLE,
          image: '/next/assets/images/exp-commercial/tenant-representation.webp',
          imgAlt: 'Real estate professionals touring a loft-style commercial property with exposed brick and modern flooring.',
          description: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_TENANTREPRESENTATION_DESCRIPTION,
          backgroundColor: '#736F68',
          isDark: true,
          link: '/our-services/tenant-representation',
        },
        {
          title: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_BUSINESSBROKERDIVISION_TITLE,
          image: '/next/assets/images/exp-commercial/business-broker-division.webp',
          imgAlt: 'Business professional presenting charts on a large screen to a group seated in a modern glass-walled conference room.',
          description: LOCALE_EXP_COMMERCIAL_PATHS.OURSERVICES_CARDS_BUSINESSBROKERDIVISION_DESCRIPTION,
          backgroundColor: '#403B3F',
          isDark: true,
          link: '/our-services/business-broker-division',
        },
      ],
    },
  };

  return homeConfig;
}